import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { Button, Dialog, XStack, YStack } from '@usepepper/sprout-kit-ui';
import {
  CustomerFilterField,
  CustomerFilters,
  CustomerPresenceFilter,
  CustomerYesNoFilter,
  DEFAULT_CUSTOMER_FILTERS,
} from '@finance-hub/domain/customers/blocs/types/customer-list';
import { CustomersFilterGroup } from '@finance-hub/domain/customers/components/customers-filter-group';

const FILTER_FIELDS = Object.keys(
  DEFAULT_CUSTOMER_FILTERS,
) as CustomerFilterField[];

export const countActiveCustomerFilters = (filters: CustomerFilters): number =>
  FILTER_FIELDS.filter(
    (field) => filters[field] !== DEFAULT_CUSTOMER_FILTERS[field],
  ).length;

const PRESENCE_OPTIONS: { labelKey: string; value: CustomerPresenceFilter }[] =
  [
    { labelKey: 'all', value: 'all' },
    { labelKey: 'finance_hub.customers.table.on_file', value: 'onFile' },
    { labelKey: 'finance_hub.customers.table.missing', value: 'missing' },
  ];

const YES_NO_OPTIONS: { labelKey: string; value: CustomerYesNoFilter }[] = [
  { labelKey: 'all', value: 'all' },
  { labelKey: 'yes', value: 'yes' },
  { labelKey: 'no', value: 'no' },
];

interface Props {
  filters: CustomerFilters;
  isOpen: boolean;
  onApply: (filters: CustomerFilters) => void;
  onClose: () => void;
}

export const CustomersFilterDialog: React.FC<Props> = ({
  filters,
  isOpen,
  onApply,
  onClose,
}) => {
  // `draft` holds what the user is picking in the dialog. It only becomes
  // real on Apply — Cancel/X just closes. A closed dialog is hidden, not
  // destroyed, so without the reset it would still remember cancelled picks
  const [draft, setDraft] = useState(filters);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const dialogJustOpened = isOpen && !prevIsOpen;

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
  }
  if (dialogJustOpened) {
    setDraft(filters);
  }

  const draftActiveCount = countActiveCustomerFilters(draft);

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    // disableRemoveScroll prevents the page from shifting while the dialog is open
    <Dialog disableRemoveScroll onOpenChange={handleOpenChange} open={isOpen}>
      <Dialog.Content paddingHorizontal={0} paddingVertical="$24">
        <Dialog.Header paddingHorizontal="$24">
          <Dialog.Title>
            {i18n.t('finance_hub.customers.filters.title')}
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Body paddingHorizontal="$24">
          <YStack gap="$16">
            <CustomersFilterGroup
              labelKey="finance_hub.customers.filters.payment_method"
              onChange={(paymentMethod) =>
                setDraft((current) => ({ ...current, paymentMethod }))
              }
              options={PRESENCE_OPTIONS}
              value={draft.paymentMethod}
            />
            <CustomersFilterGroup
              labelKey="finance_hub.customers.filters.autopay"
              onChange={(autopay) =>
                setDraft((current) => ({ ...current, autopay }))
              }
              options={YES_NO_OPTIONS}
              value={draft.autopay}
            />
            <CustomersFilterGroup
              labelKey="finance_hub.customers.filters.payment_plan"
              onChange={(paymentPlan) =>
                setDraft((current) => ({ ...current, paymentPlan }))
              }
              options={YES_NO_OPTIONS}
              value={draft.paymentPlan}
            />
            <CustomersFilterGroup
              labelKey="finance_hub.customers.filters.statements"
              onChange={(statement) =>
                setDraft((current) => ({ ...current, statement }))
              }
              options={YES_NO_OPTIONS}
              value={draft.statement}
            />
            <CustomersFilterGroup
              labelKey="finance_hub.customers.filters.contact_info"
              onChange={(contactInfo) =>
                setDraft((current) => ({ ...current, contactInfo }))
              }
              options={PRESENCE_OPTIONS}
              value={draft.contactInfo}
            />
          </YStack>
        </Dialog.Body>
        <Dialog.Footer paddingHorizontal="$24" paddingTop="$16">
          <XStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Button
              disabled={draftActiveCount === 0}
              onPress={() => setDraft(DEFAULT_CUSTOMER_FILTERS)}
              variant="tertiary"
            >
              {i18n.t('reset_all')}
            </Button>
            <XStack gap="$8">
              <Dialog.Close asChild>
                <Button variant="secondary">{i18n.t('cancel')}</Button>
              </Dialog.Close>
              <Button onPress={handleApply}>
                {draftActiveCount > 0
                  ? i18n.t('apply_with_count', { count: draftActiveCount })
                  : i18n.t('apply')}
              </Button>
            </XStack>
          </XStack>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};
